import _ from 'lodash'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { message } from 'antd'
import React from 'react'
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import weekday from 'dayjs/plugin/weekday'
import { PeopleNominatives } from '@lmc/ts-common'

// http://colorbrewer2.org/#type=diverging&scheme=RdYlBu&n=7
export const StoolColormap = {
  1: '#4575b4',
  2: '#91bfdb',
  3: '#e0f3f8',
  4: '#efefbf',
  5: '#edd187',
  6: '#fc8d59',
  7: '#d73027'
}
export function isStool({
  item
}: {
  item: string | null
}): boolean | null | '' {
  return item && item.match(/stool/i) !== null
}

export function isStoolBloody({
  description
}: {
  description: string | null
}): boolean {
  return description?.match(/was bloody/i) !== null
}

export function isStoolOffensive({
  description
}: {
  description: string | null
}): boolean {
  return description?.match(/offensive smelling/i) !== null
}

export function isStoolMucus({
  description
}: {
  description: string | null
}): boolean {
  return description?.match(/contained mucus/i) !== null
}

export function getStoolColor({
  description
}: {
  description: string | null
}): string | null {
  const p = new RegExp('stool\\swas\\s[a-zA-Z]+', 'i')
  const stoolColor = description?.match(p)
  return stoolColor ? stoolColor[0].split(' ').slice(-1)[0] : null
}
/**
 * Shows a small toast at the top of the screen. Used throughout the app for
 * confirming user actions, i.e. creating, updating, or deleting items.
 *
 * type the type of toast, such as success or warning
 * title the message to display
 * action optional action button with text and onClick
 */
export function showToast({
  type = 'success',
  title,
  action
}: {
  type?: 'success' | 'warning' | 'error' | 'loading' | 'info'
  title: string
  action?: React.ReactNode
}) {
  const duration = action ? 6 : 4

  if (type === 'success') {
    if (action) {
      message.success(
        React.createElement(
          'div',
          {
            style: {
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              gap: '8px',
              flexWrap: 'nowrap',
              minWidth: 'max-content'
            }
          },
          title,
          action
        ),
        duration
      )
    } else {
      message.success(title, duration)
    }
  } else if (type === 'warning') {
    message.warning(title, duration)
  } else if (type === 'error') {
    message.error(title, duration)
  } else if (type === 'loading') {
    message.loading(title, duration)
  }
}

export function openDialog({
  type = 'success',
  title,
  text,
  btnLabel = 'OK',
  onConfirm
}: {
  type?: 'success' | 'error' | 'warning' | 'info'
  title: string
  text: string
  btnLabel?: string
  onConfirm: () => void
}) {
  // @ts-ignore
  Swal.fire({
    title,
    html: `<div style='font-weight: 400;'>${text}</div>`,
    icon: type,
    showConfirmButton: true,
    confirmButtonText: `<div><i style="padding-right: 5px" class="octicon octicon-trashcan"></i> ${btnLabel}</div>`,
    showCancelButton: true,
    cancelButtonText: 'Cancel',
    cancelButtonClass: 'order-1 lmc-swal-cancel-button',
    confirmButtonClass: 'order-2 lmc-swal-confirm-button',
    isConfirmed: onConfirm
  }).then((result) => {
    if (result.value) onConfirm()
  })
}
export function getObjectDiff(
  object: Record<string, any>,
  base: Record<string, any>
): Record<string, any> {
  function getChanges(
    object: Record<string, any>,
    base: Record<string, any>
  ): Record<string, any> {
    return _.transform(object, (result, value, key) => {
      if (!_.isEqual(value, base[key])) {
        result[key] =
          _.isObject(value) && _.isObject(base[key]) && !_.isArray(value)
            ? getChanges(value, base[key])
            : value
      }
    })
  }
  return getChanges(object, base)
}

export function sortList(
  list: any[],
  filter: (item: any) => boolean
): any[] | null {
  if (!list) return null
  const withFullNames = list.map((row: any) => ({
    ...row,
    name: `${row.name.first} ${row.name.last}`
  }))
  return _.chain(withFullNames).sortBy('name').filter(filter).value()
}

export function formatAddress(address: {
  number?: string
  name?: string
  street1?: string
  street2?: string
  suburb?: string
  state?: string
  postcode?: string
  country?: string
}): string {
  return _.compact([
    address.number,
    address.name,
    address.street1,
    address.street2,
    address.suburb,
    address.state,
    address.postcode,
    address.country
  ]).join(', ')
}

/**
 * Recursively builds up a FormData object from nested form fields. Support
 * Arrays, plain Objects, Files, Strings, Numbers, and null values.
 *
 * @param {Object} fields includes form keys and values
 * @param {FormData} form FormData object that form values are appended to
 * @param {String} parentKey for nested fields, include the parent key to
 * derive the child key.
 */
export function buildEncodedForm(
  fields: Record<string, any>,
  form: FormData | null = null,
  parentKey: string | null = null
): FormData {
  const formData = form || new FormData()

  for (const field in fields) {
    const formKey = parentKey ? `${parentKey}[${field}]` : field
    const formValue = fields[field]

    if (formValue instanceof Array) {
      // append recursively for each array element
      if (typeof formValue[0] === 'object' && formValue[0] !== null) {
        formValue.forEach((arrayValue, idx) => {
          buildEncodedForm(arrayValue, formData, `${formKey}[${idx}]`)
        })
      } else if (formValue.length) {
        formValue.forEach((arrayValue, idx) => {
          const appendValue = arrayValue === null ? 'null' : arrayValue
          formData.append(`${formKey}[${idx}]`, appendValue)
        })
      } else {
        // @ts-ignore
        formData.append(formKey, formValue)
      }
    } else if (typeof formValue === 'object' && formValue !== null) {
      if (formValue.file instanceof File) {
        formData.append(formKey, formValue.file)
      } else {
        // append recursively for each property
        buildEncodedForm(formValue, formData, formKey)
      }
    } else if (formValue === null) {
      formData.append(formKey, 'null')
    } else {
      formData.append(formKey, formValue)
    }
  }

  return formData
}

export function getRiskLevel(
  ranges: { low: number; high: number; risk: string }[],
  score: number
): string {
  for (const range of ranges) {
    if (score >= range.low && score < range.high) {
      return range.risk
    }
  }
  return 'na'
}

/**
 * Used during componentDidUpdate lifecycle method. Checks current vs previous
 * field values for changes in the provided `valueFields`. We only want an
 * aggregate field (i.e. a 'field' containing multiple Field components)
 * to set a new value if all of its child fields have a value, and at least
 * one of those fields has changed.
 *
 * @param {Object} prevValues previous form fields
 * @param {Object} currentValues current form fields
 * @param {Array} valueFields check if changes have occurred in these fields
 */
export function shouldAggregateFieldSetValue(
  prevValues: Record<string, any> | null,
  currentValues: Record<string, any> | null,
  valueFields: string[]
): boolean {
  let hasChangedFields = false

  if (!prevValues || !currentValues) {
    return false
  }

  for (const field of valueFields) {
    if (!_.has(currentValues, field)) {
      return false
    }
    if (_.get(prevValues, field) !== _.get(currentValues, field)) {
      hasChangedFields = true
    }
  }

  return hasChangedFields
}

export function calculateBmi(weight: string | null, height: string | null) {
  if (!weight || !height) return null
  return parseFloat(
    (parseFloat(weight) / (parseFloat(height) / 100) ** 2).toFixed(1)
  )
}

// Used for generating a handover note at the end of an initial assessment
export function getNewResidentHandoverNotes({
  resident,
  overrideDate,
  contactsInformed
}: {
  resident: {
    admissionDate: string | null
    location: Record<string, any>
    name: {
      first: string
      last: string
    }
  }
  overrideDate: string | null
  contactsInformed: boolean
}): string {
  const { admissionDate, location, name } = resident

  let note = `${name.first} ${name.last} is joining us`

  if (overrideDate || admissionDate) {
    let date
    const chosenDate = overrideDate || admissionDate
    const difference = dayjs(chosenDate)
      .startOf('day')
      .diff(dayjs().startOf('day'), 'days')
    if (difference === 0) {
      note += ' today. '
    } else if (difference === 1) {
      note += ' tomorrow. '
    } else {
      date = dayjs(chosenDate).format('Do MMMM YYYY')
      note += ` on ${date}. `
    }
  } else {
    note += '. '
  }

  const locationItems = ['building', 'floor', 'room', 'bed']
  const numberOfLocationItems = location && Object.keys(location).length
  if (numberOfLocationItems) {
    note += 'They will be in '
    locationItems.forEach((item, i) => {
      const isLastItem =
        item === Object.keys(location)[numberOfLocationItems - 1]
      const hasValue = location[item] || location[item] === 0
      if (hasValue && isLastItem) {
        note += `${item}: ${location[item]}. `
      } else if (hasValue) {
        note += `${item}: ${location[item]}, `
      }
    })
  }

  if (contactsInformed) {
    note += 'Their family / social worker has been informed. '
  }

  note += 'Please read their profile so you know how to best care for them.'

  return note
}

/**
 * Important function that transforms form fields with a `dependsOn` value into
 * 'child' fields, nested inside the field that they depend on. This data
 * structure is important to maintain for the `UserGeneratedFieldLayout`
 * component, where recursive rendering is used to display dependent fields
 * directly below the fields that they are dependent on.
 *
 * @param {Array} fields form fields in a user-generated form specification,
 * created with the FormBuilder library.
 */
export function groupDependentFields(
  fields: {
    key?: string
    keys?: string[]
    dependsOnKey?: string
    dependsOnValue?: string
  }[]
): any[] {
  const withDependsOn = fields.map((field) => {
    if (field.dependsOnKey) {
      return {
        ...field,
        dependsOn: {
          key: field.dependsOnKey,
          value: field.dependsOnValue
        },
        dependsOnKey: undefined,
        dependsOnValue: undefined
      }
    }
    return field
  })

  const byDependsOnKey = _.groupBy(withDependsOn, 'dependsOn.key')
  return (
    _(withDependsOn)
      // @ts-ignore
      .filter((f) => !f.dependsOn)
      .map((field) => {
        const key = field.key || field.keys
        // @ts-ignore
        if (byDependsOnKey[key]) {
          return {
            ...field,
            // @ts-ignore
            dependentFields: byDependsOnKey[key]
          }
        }
        return field
      })
      .value()
  )
}

export function downloadPdfData(data: BlobPart, fileName: string): void {
  const blob = new Blob([data], { type: 'application/pdf' })
  const link = document.createElement('a')
  const url = window.URL.createObjectURL(blob)
  link.href = url
  link.download = `${fileName}.pdf`

  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }, 100)
}

/**
 * Used inside Sidebar to navigate to the correct path when clicking on a
 * sidebar item. The correct path is often different and applying a single rule
 * to all scenarios unfortunately isn't possible.
 *
 * @param params react-router params for the current route
 */
export function getListPath(
  currentPath: string,
  params: Record<string, any> | null
): string | undefined {
  const currentScreen = currentPath.split('/').pop()

  if (!params) return currentScreen
  if (params.reporting_id) return `charts/${params.reporting_id}`
  if (params.care_plan_id) return 'care-plans'
  if (params.body_map_report_id) return 'charts/body_maps'
  if (/charts\/body_maps/.test(currentPath)) return 'charts/body_maps'
  if (/emar\/stock/.test(currentPath)) return 'emar/stock'
  if (params.consent_form_id) return 'consent'
  if (params.medication_id) return 'emar'
  if (params.pre_assessment_id) return 'initial-assessments'
  if (params.risk_assessment_id) return 'risk-assessments'
  if (params.pre_assessment_id) return 'initial-assessments'
  if (/profile\/edit/.test(currentPath)) return 'profile'
  if (/profile\/medications/.test(currentPath)) return 'profile/medications'
  return currentScreen
}

/**
 * Generates search results for async select box
 *
 * @param {String} input user-generated input from typing
 * @param {Array} options all options for the select box
 * @param {Function} callback function to return the results to
 */
export function getSelectSearchResults(
  input: string,
  options: { searchValue: string }[],
  callback: (results: { searchValue: string }[]) => void
): void {
  if (!input) return callback([])

  const inputValue = input.toLowerCase()
  const search = new RegExp(inputValue)

  const matchedResults = options.filter((option) =>
    search.test(option.searchValue)
  )

  const sortedByMatchingStart = _.sortBy(matchedResults, (option) =>
    option.searchValue.startsWith(inputValue) ? 1 : 2
  )

  callback(_.take(sortedByMatchingStart, 200))
}

interface LogRevision {
  title: string
  description: string
  timeLogged: string
  amountType?: string
  amount1: number
  amount2: number
  isNextHandover: boolean
}

export function getLogRevisionChangelog(
  current: LogRevision,
  previous: LogRevision
): string[] {
  const result: string[] = []

  if (!_.isPlainObject(current) || !_.isPlainObject(previous)) {
    return result
  }
  if (current.title !== previous.title) {
    result.push('Title was changed')
  }
  if (current.description !== previous.description) {
    result.push('Description was changed')
  }
  if (current.timeLogged !== previous.timeLogged) {
    result.push('Time was changed')
  }

  const currentAmountType = current.amountType || ''
  const previousAmountType = previous.amountType || ''
  if (current.amount1 !== previous.amount1) {
    result.push(
      `Measurement was changed. Was ${previous.amount1}${previousAmountType}, now ${current.amount1}${currentAmountType}`
    )
  }
  if (current.amount2 !== previous.amount2) {
    result.push(
      `Measurement was changed. Was ${previous.amount2}${previousAmountType}, now ${current.amount2}${currentAmountType}`
    )
  }

  if (current.isNextHandover && !previous.isNextHandover) {
    result.push('Flagged for handover')
  }
  if (!current.isNextHandover && previous.isNextHandover) {
    result.push('Unflagged for handover')
  }

  const currentPictures = getRevisionPictures(current)
  const previousPictures = getRevisionPictures(previous)
  const currentNumberPictures = currentPictures.length
  const previousNumberPictures = previousPictures.length
  if (previousNumberPictures - currentNumberPictures === 1) {
    result.push('Photo was deleted')
  } else if (previousNumberPictures - currentNumberPictures > 1) {
    result.push('Photos were deleted')
  } else if (currentNumberPictures - previousNumberPictures === 1) {
    result.push('Photo was added')
  } else if (currentNumberPictures - previousNumberPictures > 1) {
    result.push('Photos were added')
  }

  return result
}

// @ts-ignore
const getRevisionPictures = (revision) =>
  _.compact([revision.picture1, revision.picture2, revision.picture3])

export function getNominative(
  nominative?: string | null,
  text?: string | null
): string {
  if (!nominative || !nominative.length) {
    return text || ''
  }

  if (
    nominative === PeopleNominatives.people &&
    text &&
    !(text?.charAt(text.length - 1) === 's')
  ) {
    return 'Person'
  }

  if (text && text.includes('s') && text.charAt(text.length - 1) === 's') {
    return nominative
  }
  if (nominative.charAt(nominative.length - 1) !== 's') return nominative
  return nominative.slice(0, -1)
}

export function hexToRgbA(hex: string, opacity: number = 1): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  if (opacity) {
    return `rgba(${r},${g},${b},${opacity})`
  }

  return `rgb(${r},${g},${b})`
}

export const isLowerThen564px = window.innerWidth < 564

export const objectDifference = (
  obj1: Record<string, any>,
  obj2: Record<string, any>
) =>
  _.reduce(
    obj1,
    (result, value, key) => {
      if (!_.isEqual(value, obj2[key])) {
        // @ts-ignore
        result[key] = value
      }
      return result
    },
    {}
  )

export const getPartOfDay = (time: string) => {
  const hour = parseInt(dayjs(time).format('HH'), 10)
  if (hour >= 0 && hour < 8) {
    return 'Early Morning'
  }
  if (hour >= 8 && hour < 12) {
    return 'Morning'
  }
  if (hour >= 12 && hour < 18) {
    return 'Afternoon'
  }
  return 'Evening'
}

export const formatDateToDay = (date: string) => {
  const inputDate = dayjs(date).startOf('day')
  const today = dayjs().startOf('day')
  const yesterday = dayjs().subtract(1, 'day').startOf('day')

  if (inputDate.isSame(today, 'day')) {
    return 'Today'
  }

  if (inputDate.isSame(yesterday, 'day')) {
    return 'Yesterday'
  }

  return inputDate.format('DD-MM-YYYY')
}

export const formatDate = (dateString: string | dayjs.Dayjs) => {
  dayjs.extend(isToday)
  dayjs.extend(isYesterday)
  dayjs.extend(isTomorrow)
  dayjs.extend(customParseFormat)
  dayjs.extend(weekday)

  const date = dayjs(dateString)

  if (date.isToday()) {
    return 'Today'
  }
  if (date.isYesterday()) {
    return 'Yesterday'
  }
  if (date.isTomorrow()) {
    return 'Tomorrow'
  }
  return date.format('dddd')
}

export const getFormattedTimeDifference = (dateTime: string) => {
  const timeDiff = dayjs(dateTime).diff(dayjs(), 'minute')
  const days = Math.floor(timeDiff / (60 * 24))
  const hours = Math.floor((timeDiff % (60 * 24)) / 60)
  const minutes = timeDiff % 60

  const daysString = days === 0 ? '' : `${days} day${days !== 1 ? 's' : ''}`
  const hoursString =
    hours === 0 ? '' : `${hours} hour${hours !== 1 ? 's' : ''}`
  const minutesString =
    minutes === 0
      ? ''
      : `${minutes.toString().padStart(2, '0')} minute${
          minutes !== 1 ? 's' : ''
        }`

  const formattedTimeDiff = `${daysString} ${hoursString} ${minutesString.replace(
    /^0+/,
    ''
  )}`
  return formattedTimeDiff
}

export function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}
